"use client";

import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import TokensPanel from "@/components/TokensPanel";

interface TokenBalanceProps {
  // The in-game header passes this so the pill plugs into the game's shared
  // panel state (mutually exclusive with the leaderboard). Everywhere else the
  // pill manages its own tokens panel.
  onClick?: () => void;
  // Which edge the self-managed panel drops from — match the pill's position
  // (e.g. "left" on the welcome screen where the pill sits top-left).
  panelSide?: "left" | "right";
}

// Header pill showing the signed-in player's token balance. Hidden entirely for
// guests. Greyed at zero, accent above. Clicking it opens the compact tokens
// panel (in-game via the shared state, elsewhere via its own).
export default function TokenBalance({
  onClick,
  panelSide = "right",
}: TokenBalanceProps) {
  const { user, profile, guestTokens } = useAuth();
  const [open, setOpen] = useState(false);

  // Signed-in players use their account balance; guests use the
  // localStorage-backed balance (null until hydrated — render nothing then).
  const tokens = user ? profile?.tokens ?? 0 : guestTokens;
  if (tokens === null) return null;

  const hasTokens = tokens > 0;
  const className = `flex items-center gap-1.5 rounded-full border bg-background/80 px-2.5 py-1.5 text-[0.65rem] font-bold uppercase tracking-widest backdrop-blur transition md:px-4 md:py-2 md:text-xs ${
    hasTokens
      ? "border-accent/50 text-accent hover:border-accent"
      : "border-white/20 text-white/40 hover:border-white/40 hover:text-white/60"
  }`;

  return (
    <>
      <button
        onClick={onClick ?? (() => setOpen((o) => !o))}
        title="Get tokens"
        className={className}
      >
        <span aria-hidden className="text-sm leading-none">
          🪙
        </span>
        <span className="font-mono">{tokens.toLocaleString()}</span>
      </button>
      {/* Self-managed panel only when not wired to the game's shared state. */}
      {!onClick && (
        <TokensPanel open={open} onClose={() => setOpen(false)} side={panelSide} />
      )}
    </>
  );
}
